import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { slotStatsApi } from '../../lib/api';
import { classifyR14Error, r14ErrorText } from '../../lib/r14Errors';
import {
  normaliseCloseFriends, cfState, closeFriendsHistory, capShare, splitStamp, readCfReport, CF_COLOR, CF_KEY,
} from '../../lib/closeFriendsView';
import { fmtInt, fmtDay, fmtSpan, fmtServerDateTime, fmtRate, plural } from '../../lib/slotStatsView';
import { ChartCard, DataTableView, EmptyPanel } from './ChartKit';
import { ActionsChart } from './ActionsCharts';
import {
  StateChip, Figure, CompactLine, logHidden, logFailed, NOT_RECORDED,
} from './R15Parts';

/*
 * Round 15 (2026-09-30), Varun item 6: the Close Friends card of the Slot Stats page (lib/closeFriendsView.js has
 * the server contract).
 *
 *   - GET /uiapi/slots/{slot}/close-friends answers 404 / 405 (a server before round 15) or 404 not_enabled (its
 *     part is switched off on the server - the default until the live session passes): NOTHING is drawn, exactly
 *     the page before round 15 (and a line in the console says why).
 *   - 716 not ticked on this slot (state OFF): one compact line.
 *   - Otherwise: the state (the server's sentence, with a short name on a chip), today's adds out of the day's
 *     limit, the total, the list size (when it was read) against the cap (721), the waiting queue, yesterday /
 *     7 / 30 / 90 days, the last report, and "Close Friends added" day by day over the page's range, in the
 *     page's bar style with its own colour (stats.css --viz-cf). It is NOT an action type: never in the "Actions
 *     done" stack, its totals, the daily table or the actions-done ratios.
 * Every missing value reads "not recorded", never 0.
 */

const val = (v) => (v === null || v === undefined ? null : fmtInt(v));

/** The parts that are there, joined by " · " (null when none is). */
function joinDot(parts) {
  const kept = parts.filter(Boolean);
  if (!kept.length) return null;
  return kept.map((p, i) => <React.Fragment key={i}>{i ? ' · ' : null}{p}</React.Fragment>);
}
const orNr = (v) => (v === null || v === undefined ? NOT_RECORDED : fmtInt(v));

function Detail({ label, children, testid }) {
  return (
    <div className="grid gap-x-4 gap-y-0.5 py-2 sm:grid-cols-[13rem_minmax(0,1fr)]" data-testid={testid}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-sm text-foreground text-pretty">{children}</dd>
    </div>
  );
}

export default function CloseFriendsCard({ slotName, statsRaw, statsLoading = false, range = 30, now }) {
  const q = useQuery({
    queryKey: ['closeFriends', slotName],
    queryFn: () => slotStatsApi.closeFriends(slotName).then((r) => r.data),
    retry: false,
  });
  if (q.isLoading) return null;
  if (q.error) {
    const c = classifyR14Error(q.error);
    if (c.kind === 'routeMissing' || c.kind === 'notEnabled') {
      logHidden('Close Friends', slotName, c.kind === 'notEnabled'
        ? `switched off on this server (${c.message || 'not_enabled'})` : `no /close-friends route on this server (HTTP ${c.status})`);
      return null;
    }
    logFailed('Close Friends', slotName, c);
    return (
      <CompactLine icon={Star} title="Close Friends" testid="close-friends-error" tone="error">
        {c.kind === 'slotNotFound'
          ? (c.message || `No slot ${slotName} on this account.`)
          : `The figures could not be loaded (${r14ErrorText(q.error, 'no answer')}).`}
      </CompactLine>
    );
  }
  const cf = normaliseCloseFriends(q.data);
  if (!cf) {
    logHidden('Close Friends', slotName, 'the answer carries no Close Friends fields');
    return null;
  }
  const clock = now || new Date();
  const st = cfState(cf, clock);
  if (st.code === 'OFF') {
    return (
      <CompactLine icon={Star} title="Close Friends" testid="close-friends-off">
        {cf.stateText || 'Off (716 is not ticked)'}
        {cf.totalAdded !== null ? ` · ${fmtInt(cf.totalAdded)} added in total` : ''}
      </CompactLine>
    );
  }

  const h = closeFriendsHistory(statsRaw, range, clock);
  const span = h.days.length ? fmtSpan(h.days[0], h.days[h.days.length - 1]) : null;
  const share = capShare(cf);
  const report = splitStamp(cf.lastReport);
  // the worker's line as a sentence; its raw form stays under "details" (null: shown as it is, one console warning)
  const readReport = report ? readCfReport(report.text) : null;
  const reportSentence = readReport && readReport.pieces.length ? readReport.pieces.join(' · ') : null;
  const per = h.weekly ? 'week' : 'day';

  const table = h.present && !h.allZero ? (
    <DataTableView caption={`Close Friends added per ${per}`} testid="close-friends-datatable"
      columns={[
        { key: 'date', label: h.weekly ? 'Week' : 'Date', render: (r) => (h.weekly ? fmtSpan(r.from, r.to) : fmtDay(r.date, 'long')) },
        {
          key: 'value', label: 'Close Friends added', align: 'right',
          render: (r) => (r.value === null ? NOT_RECORDED : `${fmtInt(r.value)}${r.running ? ' (so far)' : ''}`),
        },
      ]}
      rows={[...h.bars].reverse()} />
  ) : null;

  let chart;
  if (!h.sent) {
    chart = (
      <EmptyPanel testid={statsLoading ? 'close-friends-chart-loading' : 'close-friends-chart-unavailable'}>
        {statsLoading ? 'Loading the day-by-day history...' : 'The day-by-day history is not available from this server.'}
      </EmptyPanel>
    );
  } else if (!h.present) {
    chart = <EmptyPanel testid="close-friends-chart-empty">No Close Friends adds recorded for this slot yet.</EmptyPanel>;
  } else if (h.allZero) {
    chart = <EmptyPanel testid="close-friends-chart-empty">No Close Friends added in {span}: every recorded {per} is 0.</EmptyPanel>;
  } else {
    chart = (
      <ActionsChart bars={h.bars} metric={{ key: CF_KEY, label: 'Close Friends added' }} avg={h.avg} weekly={h.weekly}
        color={CF_COLOR} height={220} testid="close-friends-chart" />
    );
  }

  const footer = [];
  if (h.present && !h.allZero && h.partial) {
    footer.push(
      <p key="partial" data-testid="close-friends-partial-note">
        {h.weekly
          ? 'The lighter bar includes today so far: the day is not over.'
          : 'The lighter bar is today so far: the day is not over, so it is left out of the average.'}
      </p>,
    );
  }
  if (h.present && h.day0Known && h.day0 !== h.today) {
    footer.push(
      <p key="day0" data-testid="close-friends-counter-day-note">
        The slot&apos;s counters are on {fmtDay(h.day0, 'medium')}: it has not started a newer day since.
      </p>,
    );
  }

  return (
    <ChartCard icon={Star} title="Close Friends" testid="close-friends-card"
      badge={<StateChip label={st.label} tone={st.tone} code={st.code} testid="close-friends-state" />}
      subtitle={`716 "Add New Followers To Close Friends": ${cf.addNewFollowers ? 'ON' : 'OFF'} · 717 backfill: ${cf.backfill ? 'ON' : 'OFF'}. `
        + 'Only adds the worker saw ticked twice and saved with Done are counted (460\'s verified adds included).'}
      table={table}
      controls={(
        <div className="space-y-3">
          <p className="text-sm text-foreground text-pretty" data-testid="close-friends-state-text">{st.text || NOT_RECORDED}</p>
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            <Figure label="Today" testid="close-friends-today" value={val(cf.addedToday)}
              suffix={cf.todayLimit !== null ? `of ${fmtInt(cf.todayLimit)}` : null}
              caption={joinDot([
                cf.perDayMin !== null && cf.perDayMax !== null
                  ? `Today's limit, picked between ${fmtInt(cf.perDayMin)} and ${fmtInt(cf.perDayMax)}` : null,
                cf.perSessionMin !== null && cf.perSessionMax !== null
                  ? <span key="s" className="whitespace-nowrap">{fmtInt(cf.perSessionMin)}-{fmtInt(cf.perSessionMax)} a session</span> : null,
              ])} />
            <Figure label="Total added" testid="close-friends-total" value={val(cf.totalAdded)} />
            <Figure label="List" testid="close-friends-list" value={val(cf.listSize)} suffix={cf.listSize !== null ? 'people' : null}
              caption={joinDot([
                cf.listSizeAt ? `Read ${fmtServerDateTime(cf.listSizeAt)}` : null,
                cf.cap !== null ? <span key="c" className="whitespace-nowrap">cap {fmtInt(cf.cap)}</span> : null,
              ])}>
              {share !== null && (
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/[.06]" role="img"
                  aria-label={`${fmtInt(cf.listSize)} of the ${fmtInt(cf.cap)} cap`} data-testid="close-friends-cap-bar">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(2, share * 100)}%`, background: share >= 1 ? 'hsl(var(--warning))' : CF_COLOR }} />
                </div>
              )}
            </Figure>
            <Figure label="Waiting in the queue" testid="close-friends-queued" value={val(cf.queued)} />
          </div>
          <h3 className="pt-2 text-sm font-semibold text-foreground" data-testid="close-friends-chart-title">
            Close Friends added per {per}{span ? `, ${span}` : ''}
            {h.present && !h.allZero && h.total !== null && (
              <span className="ml-2 font-normal text-muted-foreground tnum" data-testid="close-friends-range-total">
                {fmtInt(h.total)} in {plural(h.days.length, 'day')}
                {!h.weekly && h.avg !== null ? ` · ${fmtRate(h.avg, { signed: false })} a day on average` : ''}
              </span>
            )}
          </h3>
        </div>
      )}
      after={(
        <dl className="mt-4 divide-y divide-white/5 border-t border-white/5" data-testid="close-friends-details">
          <Detail label="Yesterday / last 7 / 30 / 90 days" testid="close-friends-periods">
            <span className="tnum">{orNr(cf.yesterday)} / {orNr(cf.last7)} / {orNr(cf.last30)} / {orNr(cf.last90)}</span>
            {cf.daysRecorded !== null && <span className="text-muted-foreground"> ({plural(cf.daysRecorded, 'day')} recorded)</span>}
          </Detail>
          {cf.listFullAt && (
            <Detail label="Instagram said the list is full" testid="close-friends-list-full-at">{fmtServerDateTime(cf.listFullAt)}</Detail>
          )}
          {cf.alertAt && <Detail label="Alert raised" testid="close-friends-alert-at">{fmtServerDateTime(cf.alertAt)}</Detail>}
          {cf.settingsNotes.length > 0 && (
            <Detail label="Settings in effect" testid="close-friends-settings-notes">{cf.settingsNotes.join('; ')}</Detail>
          )}
          <Detail label="Last report" testid="close-friends-report">
            {report ? (
              <>
                {report.at && <span className="mr-2 text-muted-foreground tnum">{fmtServerDateTime(report.at)}</span>}
                {reportSentence ? (
                  <>
                    <span className="break-words" title={report.text} data-testid="close-friends-report-sentence">{reportSentence}</span>
                    <details className="mt-1 text-xs text-muted-foreground" data-testid="close-friends-report-details">
                      <summary className="w-fit cursor-pointer select-none hover:text-foreground">details</summary>
                      <span className="mt-1 block break-words font-mono" data-testid="close-friends-report-raw">{report.text}</span>
                    </details>
                  </>
                ) : <span className="break-words" data-testid="close-friends-report-raw">{report.text}</span>}
              </>
            ) : <span className="text-muted-foreground">{NOT_RECORDED}</span>}
          </Detail>
        </dl>
      )}
      footer={footer.length ? footer : null}>
      {chart}
    </ChartCard>
  );
}
